import React, { useEffect, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import logoSrc from "../../Images/logo/isbm-university.webp";
/**
 * ApplicationPdfModal
 * Props:
 *  - show: boolean
 *  - onHide: fn
 *  - data: the submitted form JSON (see user's example)
 */
export default function ApplicationPdfModal({ show, onHide, data,onProceed }) {
  /* -------------------------- helpers & mappers -------------------------- */
  const LOGO_MAP = {
    ISBM: "/assets/images/logos/isbm.png",
    ISBMU: "../../Images/logo/isbm-university.webp",
    ISTM: "/assets/images/logos/istm.png",
    DEFAULT: "/assets/images/logos/default.png",
  };
  function normalizeEntityName(s) {
    const t = String(s || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
    if (t.includes("ISBMU") || t.includes("ISBMUNIVERSITY")) return "ISBMU";
    if (t.includes("ISTM")) return "ISTM";
    if (t.includes("ISBM")) return "ISBM";
    return "DEFAULT";
  }
  function resolveLogo(widgetName) {
    const key = normalizeEntityName(widgetName);
    return LOGO_MAP[key] || LOGO_MAP.DEFAULT;
  }

  function byId(id) {
    return document.getElementById(id);
  }
  function setText(id, val) {
    const el = byId(id);
    if (el) el.textContent = val ?? "";
  }
  function setVal(id, val) {
    const el = byId(id);
    if (!el) return;
    el.value = val ?? "";
    el.setAttribute("value", el.value);
  }
  function setChecked(id, bool) {
    const el = byId(id);
    if (!el) return;
    el.checked = !!bool;
    if (el.checked) el.setAttribute("checked", "checked");
    else el.removeAttribute("checked");
  }

  function applyFormArray(arr) {
    arr.forEach(({ id, value, kind }) => {
      switch (kind) {
        case "value":
          setVal(id, value);
          break;
        case "check":
          setChecked(id, value);
          break;
        default:
          setText(id, value);
      }
    });
  }

  // Formatters
  const upper = (s) => (s ? String(s).toUpperCase() : "");
  const safe = (s) => s ?? "";

  const dobParts = useMemo(() => {
    let d = safe(data?.dob);
    console.log(data?.dob);
    // expecting YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [yy, mm, dd] = d.split("-");
      return { dd, mm, yy };
    }
    return { dd: "", mm: "", yy: "" };
  }, [data]);

  // Academic rows map for quick lookup
  const acad = useMemo(() => {
    const defaults = {
      "10th": {},
      "12th": {},
      Graduation: {},
      "Post Graduation": {},
      "Any Other Qualification": {},
    };
    (data?.academicDetails || []).forEach((row) => {
      if (row?.examPassed && defaults[row.examPassed] !== undefined) {
        defaults[row.examPassed] = row;
      }
    });
    return defaults;
  }, [data]);

  // Build the array of {id, value, kind} matching your HTML template
  const formDataArray = useMemo(() => {
    const isUG =
      (data?.level || "").toLowerCase().includes("under") ||
      (data?.category || "").toLowerCase().includes("under");
    const isPG =
      (data?.level || "").toLowerCase().includes("post") ||
      (data?.category || "").toLowerCase().includes("post");

    const mediumHindi = data?.formHorizontalRadiosMedium === "mid_hindi";
    const mediumEnglish = data?.formHorizontalRadiosMedium === "mid_english";

    const schemeSem = data?.formHorizontalRadiosSC === "sem-mode";
    const schemeYear = data?.formHorizontalRadiosSC === "year-mode";

    const courseFull = data?.formHorizontalRadiosCT === "full-time";
    const coursePart = data?.formHorizontalRadiosCT === "part-time";

    const lateralYes = data?.formHorizontalRadiosLE === "yes";
    const lateralNo = data?.formHorizontalRadiosLE === "no";

    const maritalSingle = data?.formHorizontalRadiosMS === "single";
    const maritalMarried = data?.formHorizontalRadiosMS === "married";

    const genderMale = data?.formHorizontalRadios === "male";
    const genderFemale = data?.formHorizontalRadios === "female";

    const provYes = data?.formHorizontalRadiosPA === "yes";
    const provNo = data?.formHorizontalRadiosPA === "no";

    const cat = safe(data?.formHorizontalRadiosCC);

    // Payment – treat positive amount as online for example (you can adapt)
    const payOnline = !!data?.payAmount;
    const getFirst = (arr) => (Array.isArray(arr) && arr.length ? arr[0] : {});
    const perm = getFirst(data?.addresses_permanent);
    const corr = getFirst(data?.addresses_correspondence);

    return [
      // Header
      { id: "app_no", value: safe(data?.leadId) || "", kind: "value" },
      { id: "reg_no", value: "", kind: "value" },

      // Program
      { id: "chk_ug", value: isUG, kind: "check" },
      { id: "chk_pg", value: isPG, kind: "check" },
      { id: "chk_other", value: !(isUG || isPG), kind: "check" },
      { id: "course_name", value: safe(data?.course) },
      { id: "spec1", value: safe(data?.specialization) },
      { id: "spec2", value: "" },
      { id: "chk_mo_hin", value: mediumHindi, kind: "check" },
      { id: "chk_mo_eng", value: mediumEnglish, kind: "check" },
      { id: "admission_session", value: "" },
      { id: "chk_lat_yes", value: lateralYes, kind: "check" },
      { id: "chk_lat_no", value: lateralNo, kind: "check" },

      // Mid panel mirrors
      { id: "mid_hindi", value: mediumHindi, kind: "check" },
      { id: "mid_english", value: mediumEnglish, kind: "check" },
      { id: "chk_sem", value: schemeSem, kind: "check" },
      { id: "chk_year", value: schemeYear, kind: "check" },
      { id: "chk_full", value: courseFull, kind: "check" },
      { id: "chk_part", value: coursePart, kind: "check" },

      // Personal
      { id: "cand_name", value: upper(data?.fullName) },
      { id: "father_name", value: upper(data?.fatherName) },
      { id: "mother_name", value: upper(data?.motherName) },
      { id: "gender_m", value: genderMale, kind: "check" },
      { id: "gender_f", value: genderFemale, kind: "check" },
      { id: "dob_dd", value: dobParts.dd },
      { id: "dob_mm", value: dobParts.mm },
      { id: "dob_yy", value: dobParts.yy },

      // Category/Nationality/Marital
      { id: "cat_gen", value: cat === "GEN", kind: "check" },
      { id: "cat_sc", value: cat === "SC", kind: "check" },
      { id: "cat_min", value: cat === "Minority", kind: "check" },
      { id: "cat_obc", value: cat === "OBC", kind: "check" },
      { id: "cat_st", value: cat === "ST", kind: "check" },
      {
        id: "nat_indian",
        value: (data?.nationality || "").toLowerCase() === "indian",
        kind: "check",
      },
      {
        id: "nat_others",
        value:
          (data?.nationality || "").toLowerCase() === "indian"
            ? ""
            : safe(data?.nationality),
      },
      { id: "mar_single", value: maritalSingle, kind: "check" },
      { id: "mar_married", value: maritalMarried, kind: "check" },

      // Permanent address
      { id: "p_addr1", value: safe(perm.line1) },
      { id: "p_addr2", value: safe(perm.line2) },
      { id: "p_area", value: safe(perm.line3) },
      { id: "p_city", value: safe(perm.city) },
      { id: "p_state", value: safe(perm.state) },
      { id: "p_pin", value: safe(perm.pincode) },
      { id: "p_country", value: safe(perm.country) },

      // Correspondence address
      { id: "c_addr1", value: safe(corr.line1) },
      { id: "c_addr2", value: safe(corr.line2) },
      { id: "c_area", value: safe(corr.line3) },
      { id: "c_city", value: safe(corr.city) },
      { id: "c_state", value: safe(corr.state) },
      { id: "c_pin", value: safe(corr.pincode) },
      { id: "c_country", value: safe(corr.country) },

      // Contact
      { id: "email_caps", value: upper(data?.email) },
      { id: "ccode", value: safe(data?.country_code) },
      { id: "mobile", value: safe(data?.c_number) },
      { id: "landline", value: safe(data?.telephone) },

      // Education 10th
      { id: "edu_10_stream", value: safe(acad["10th"].stream) },
      { id: "edu_10_inst", value: safe(acad["10th"].institution) },
      { id: "edu_10_year", value: safe(acad["10th"].yearOfPassing) },
      { id: "edu_10_sub", value: safe(acad["10th"].subjects) },
      { id: "edu_10_marks", value: safe(acad["10th"].marksObtained) },
      { id: "edu_10_max", value: safe(acad["10th"].maxMarks) },
      { id: "edu_10_pct", value: safe(acad["10th"].percentage) },

      // Education 12th
      { id: "edu_12_stream", value: safe(acad["12th"].stream) },
      { id: "edu_12_inst", value: safe(acad["12th"].institution) },
      { id: "edu_12_year", value: safe(acad["12th"].yearOfPassing) },
      { id: "edu_12_sub", value: safe(acad["12th"].subjects) },
      { id: "edu_12_marks", value: safe(acad["12th"].marksObtained) },
      { id: "edu_12_max", value: safe(acad["12th"].maxMarks) },
      { id: "edu_12_pct", value: safe(acad["12th"].percentage) },

      // Graduation
      { id: "edu_grad_stream", value: safe(acad["Graduation"].stream) },
      { id: "edu_grad_inst", value: safe(acad["Graduation"].institution) },
      { id: "edu_grad_year", value: safe(acad["Graduation"].yearOfPassing) },
      { id: "edu_grad_sub", value: safe(acad["Graduation"].subjects) },
      { id: "edu_grad_marks", value: safe(acad["Graduation"].marksObtained) },
      { id: "edu_grad_max", value: safe(acad["Graduation"].maxMarks) },
      { id: "edu_grad_pct", value: safe(acad["Graduation"].percentage) },

      // Post Graduation
      { id: "edu_pg_stream", value: safe(acad["Post Graduation"].stream) },
      { id: "edu_pg_inst", value: safe(acad["Post Graduation"].institution) },
      { id: "edu_pg_year", value: safe(acad["Post Graduation"].yearOfPassing) },
      { id: "edu_pg_sub", value: safe(acad["Post Graduation"].subjects) },
      {
        id: "edu_pg_marks",
        value: safe(acad["Post Graduation"].marksObtained),
      },
      { id: "edu_pg_max", value: safe(acad["Post Graduation"].maxMarks) },
      { id: "edu_pg_pct", value: safe(acad["Post Graduation"].percentage) },

      // Any Other
      {
        id: "edu_other_stream",
        value: safe(acad["Any Other Qualification"].stream),
      },
      {
        id: "edu_other_inst",
        value: safe(acad["Any Other Qualification"].institution),
      },
      {
        id: "edu_other_year",
        value: safe(acad["Any Other Qualification"].yearOfPassing),
      },
      {
        id: "edu_other_sub",
        value: safe(acad["Any Other Qualification"].subjects),
      },
      {
        id: "edu_other_marks",
        value: safe(acad["Any Other Qualification"].marksObtained),
      },
      {
        id: "edu_other_max",
        value: safe(acad["Any Other Qualification"].maxMarks),
      },
      {
        id: "edu_other_pct",
        value: safe(acad["Any Other Qualification"].percentage),
      },

      // Provisional
      { id: "prov_yes", value: provYes, kind: "check" },
      { id: "prov_no", value: provNo, kind: "check" },

      // Payment (simplified)
      { id: "pay_online", value: payOnline, kind: "check" },
      { id: "pay_online_no", value: safe(data?.transactionId || "") },
      { id: "pay_online_amt", value: String(data?.payAmount ?? "") },
      { id: "pay_online_date", value: safe(data?.paymentDate || "") },
      { id: "pay_online_bank", value: safe(data?.bankName || "") },

      // Declaration
      { id: "decl_name", value: upper(data?.fullName) },
      { id: "decl_father", value: upper(data?.fatherName) },
      { id: "decl_sign", value: upper(data?.fullName) },
      { id: "decl_date", value: "" },
    ];
  }, [data, dobParts, acad]);

  useEffect(() => {
    if (!show) return;
    // delay a tick to ensure DOM inside modal is mounted
    const tid = setTimeout(() => applyFormArray(formDataArray), 0);
    return () => clearTimeout(tid);
  }, [show, formDataArray]);

  function handlePrint() {
    const html = Array.from(document.querySelectorAll(".sheet"))
      .map((el) => el.outerHTML)
      .join("");
    const win = window.open("", "_blank");
    win.document.write("<html><head><title>Print</title>");
    win.document.write(`
      <style>
        @page { size: A4; margin: 0; }
        html, body { margin:0; background:#f5f6f8; font:14px/1.4 Arial,sans-serif; color:#222; height:100%; }
        .sheet { width:210mm; min-height:297mm; margin:0mm auto; background:#fff; box-shadow:0 4px 18px rgba(0,0,0,.15); display:flex; flex-direction:column; padding:5mm; }
        .sheet:last-child { page-break-after:auto; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      </style>
    `);
    win.document.write("</head><body>");
    win.document.write(html);
    win.document.write("</body></html>");
    win.document.close();
    win.focus();
    win.print();
    win.close();
  }

  // const logoSrc = "../../Images/logo/isbm-university.webp";

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      dialogClassName="modal-xxl"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Application Form Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ maxHeight: "80vh", overflowY: "auto", background: "#f5f6f8" }}
      >
        {/* ------------------------------ PAGE 1 ------------------------------ */}
        <div
          className="sheet"
          style={{
            width: "210mm",
            minHeight: "297mm",
            margin: "0mm auto",
            background: "#fff",
            boxShadow: "0 4px 18px rgba(0,0,0,.15)",
            display: "flex",
            flexDirection: "column",
            padding: "5mm",
          }}
        >
          <div
            className="sheet__inner"
            style={{
              padding: "5mm",
              border: "solid",
              borderLeft: "solid 1.5mm",
              borderRight: "solid 1.5mm",
            }}
          >
            {/* HEADER */}
            <header
              className="app-header"
              style={{
                display: "grid",
                gridTemplateColumns: "110mm 90mm",
                gap: "14px",
                alignItems: "flex-start",
              }}
            >
              <div
                className="hdr-left"
                style={{
                  paddingRight: "12px",
                  position: "relative",
                  border: "solid 2px",
                  borderBottom: "none",
                  borderTop: "none",
                  borderLeft: "none",
                }}
              >
                <img
                  className="logo"
                  src={logoSrc}
                  alt="ISBM University"
                  style={{ width: "72mm", height: "25mm", display: "block" }}
                />
                <div
                  className="uni-addr"
                  style={{
                    fontSize: "7.2px",
                    color: "#333",
                    marginTop: "4px",
                    paddingLeft: "1.4rem",
                  }}
                >
                  Nawapara | Tehsil : Chhuria | District : Gariyaband - 493996 |
                  Chhattisgarh
                </div>
              </div>
              <div
                className="hdr-right"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  borderRight: "2px",
                }}
              >
                <h1
                  className="title"
                  style={{ fontSize: "32px", margin: "0 0 2px" }}
                >
                  APPLICATION FORM
                </h1>
                <table
                  className="kv"
                  style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: "0 0px",
                  }}
                >
                  <tbody>
                    <tr>
                      <td
                        className="k"
                        style={{
                          fontWeight: 700,
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Application No. :
                      </td>
                      <td>
                        <input
                          id="app_no"
                          className="input w-md"
                          type="text"
                          defaultValue=""
                          readOnly
                          style={{
                            height: "28px",
                            border: "1px solid #4a4a4a",
                            padding: "2px 8px",
                            font: "13px Arial",
                            borderRadius: "3px",
                            width: "53mm",
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="k"
                        style={{
                          fontWeight: 700,
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Registration No. :
                      </td>
                      <td>
                        <input
                          id="reg_no"
                          className="input w-md"
                          type="text"
                          placeholder="(to be filled by office)"
                          style={{
                            height: "28px",
                            border: "1px solid #4a4a4a",
                            padding: "2px 8px",
                            font: "13px Arial",
                            borderRadius: "3px",
                            width: "53mm",
                          }}
                          defaultValue=""
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </header>

            {/* TWO BOXES */}
            <section
              className="row-2col"
              style={{
                display: "grid",
                gridTemplateColumns: "1.7fr 1fr",
                gap: "10px",
                marginTop: "10px",
              }}
            >
              {/* LEFT COMBO */}
              <div
                className="combo-box"
                style={{
                  border: "1px solid #000",
                  display: "grid",
                  gridTemplateColumns: "122mm 35mm",
                  fontSize: "10px",
                  lineHeight: 1.11,
                  height: "185px",
                }}
              >
                {/* PROGRAM LEFT */}
                <div
                  className="program-left"
                  style={{
                    padding: "6px 10px 8px",
                    borderRight: "1px solid #000",
                  }}
                >
                  <div
                    className="row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      margin: "2px 0",
                      marginTop: "2px",
                    }}
                  >
                    <span
                      className="label"
                      style={{ fontWeight: 700, fontSize: "12px" }}
                    >
                      PROGRAM APPLIED FOR
                    </span>
                  </div>

                  <div
                    className="row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      margin: "2px 0",
                    }}
                  >
                    <span>UG</span>
                    <input
                      id="chk_ug"
                      type="checkbox"
                      style={{
                        width: "12px",
                        height: "12px",
                        verticalAlign: "middle",
                      }}
                    />
                    <span>PG</span>
                    <input
                      id="chk_pg"
                      type="checkbox"
                      style={{
                        width: "12px",
                        height: "12px",
                        verticalAlign: "middle",
                      }}
                    />
                    <span>Other</span>
                    <input
                      id="chk_other"
                      type="checkbox"
                      style={{
                        width: "12px",
                        height: "12px",
                        verticalAlign: "middle",
                      }}
                    />
                    <span style={{ fontWeight: 600, marginLeft: "10px" }}>
                      Course Name:
                    </span>
                    <span
                      id="course_name"
                      style={{
                        borderBottom: "1px dotted #000",
                        height: "16px",
                        flex: 1,
                      }}
                    ></span>
                  </div>

                  <div
                    className="row"
                    style={{
                      fontSize: "11px",
                      margin: "2px 0",
                      marginTop: "4px",
                    }}
                  >
                    (for Course list see the Information Brochure )
                  </div>

                  <div
                    className="row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      margin: "2px 0",
                      marginTop: "6px",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>Specialization :</span>
                    <span>(1)</span>
                    <span
                      id="spec1"
                      style={{
                        borderBottom: "1px dotted #000",
                        height: "16px",
                        flex: 1,
                      }}
                    ></span>
                  </div>
                  <div
                    className="row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      margin: "2px 0",
                    }}
                  >
                    <span style={{ width: "74px" }}></span>
                    <span>(2)</span>
                    <span
                      id="spec2"
                      style={{
                        borderBottom: "1px dotted #000",
                        height: "16px",
                        flex: 1,
                      }}
                    ></span>
                  </div>

                  <div
                    className="row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      margin: "2px 0",
                      marginTop: "8px",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      Medium of Instruction :
                    </span>
                    <span>Hindi</span>
                    <input
                      id="chk_mo_hin"
                      type="checkbox"
                      style={{
                        width: "12px",
                        height: "12px",
                        verticalAlign: "middle",
                      }}
                    />
                    <span>English</span>
                    <input
                      id="chk_mo_eng"
                      type="checkbox"
                      style={{
                        width: "12px",
                        height: "12px",
                        verticalAlign: "middle",
                      }}
                    />
                    <span style={{ fontWeight: 600, marginLeft: "10px" }}>
                      Admission Session :
                    </span>
                    <span
                      id="admission_session"
                      style={{
                        display: "inline-block",
                        borderBottom: "1px dotted #000",
                        minWidth: "130px",
                        height: "14px",
                        verticalAlign: "baseline",
                      }}
                    ></span>
                  </div>

                  <div
                    className="row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      margin: "2px 0",
                      marginTop: "6px",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>Lateral Entry :</span>
                    <span>Yes</span>
                    <input
                      id="chk_lat_yes"
                      type="checkbox"
                      style={{
                        width: "12px",
                        height: "12px",
                        verticalAlign: "middle",
                      }}
                    />
                    <span>No</span>
                    <input
                      id="chk_lat_no"
                      type="checkbox"
                      style={{
                        width: "12px",
                        height: "12px",
                        verticalAlign: "middle",
                      }}
                    />
                  </div>
                </div>

                {/* MID PANEL */}
                <div className="program-mid" style={{ display: "block" }}>
                  <div className="mid-wrap" style={{ height: "100%" }}>
                    <div
                      className="mid-group"
                      style={{ borderTop: "none", padding: "4px 8px" }}
                    >
                      <span
                        className="mid-title"
                        style={{
                          display: "block",
                          background: "#f2f2f2",
                          fontWeight: 700,
                          border: "1px solid #000",
                          borderTop: "none",
                          borderLeft: "none",
                          borderRight: "none",
                          margin: "0 -8px 0px -8px",
                          padding: "2px 8px",
                          fontSize: "11px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Medium of Instruction
                      </span>
                      <div
                        className="mid-item"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>Hindi</span>
                        <input
                          id="mid_hindi"
                          type="checkbox"
                          style={{
                            width: "12px",
                            height: "12px",
                            verticalAlign: "middle",
                          }}
                        />
                      </div>
                      <div
                        className="mid-item"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>English</span>
                        <input
                          id="mid_english"
                          type="checkbox"
                          style={{
                            width: "12px",
                            height: "12px",
                            verticalAlign: "middle",
                          }}
                        />
                      </div>
                    </div>

                    <div
                      className="mid-group"
                      style={{
                        borderTop: "1px solid #000",
                        padding: "4px 8px",
                      }}
                    >
                      <span
                        className="mid-title"
                        style={{
                          display: "block",
                          background: "#f2f2f2",
                          fontWeight: 700,
                          border: "1px solid #000",
                          borderTop: "none",
                          borderLeft: "none",
                          borderRight: "none",
                          margin: "0 -8px 0px -8px",
                          padding: "2px 8px",
                          fontSize: "11px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Study Scheme
                      </span>
                      <div
                        className="mid-item"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>Sem Mode</span>
                        <input
                          id="chk_sem"
                          type="checkbox"
                          style={{
                            width: "12px",
                            height: "12px",
                            verticalAlign: "middle",
                          }}
                        />
                      </div>
                      <div
                        className="mid-item"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>Year Mode</span>
                        <input
                          id="chk_year"
                          type="checkbox"
                          style={{
                            width: "12px",
                            height: "12px",
                            verticalAlign: "middle",
                          }}
                        />
                      </div>
                    </div>

                    <div
                      className="mid-group"
                      style={{
                        borderTop: "1px solid #000",
                        padding: "4px 8px",
                      }}
                    >
                      <span
                        className="mid-title"
                        style={{
                          display: "block",
                          background: "#f2f2f2",
                          fontWeight: 700,
                          border: "1px solid #000",
                          borderTop: "none",
                          borderLeft: "none",
                          borderRight: "none",
                          margin: "0 -8px 0px -8px",
                          padding: "2px 8px",
                          fontSize: "11px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Course Type
                      </span>
                      <div
                        className="mid-item"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>Full time</span>
                        <input
                          id="chk_full"
                          type="checkbox"
                          style={{
                            width: "12px",
                            height: "12px",
                            verticalAlign: "middle",
                          }}
                        />
                      </div>
                      <div
                        className="mid-item"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>Part time</span>
                        <input
                          id="chk_part"
                          type="checkbox"
                          style={{
                            width: "12px",
                            height: "12px",
                            verticalAlign: "middle",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* PHOTO BOX */}
              <div
                className="photo-box"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  className="photo-rect"
                  style={{
                    width: "100%",
                    minHeight: "185px",
                    border: "1px solid #000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    fontSize: "13px",
                  }}
                >
                  Passport Size
                  <br />
                  Photograph
                </div>
              </div>
            </section>

            <section style={{ marginTop: "14px" }}>
              {/* 1 NAME */}
              <div style={{ fontWeight: 700, fontSize: "12px" }}>
                1. NAME OF THE CANDIDATE{" "}
                <span style={{ fontWeight: 400, fontSize: "12px" }}>
                  (as in candidate's Class X marksheet or equivalent certificate
                  - enclose photocopy)
                </span>
              </div>
              <div
                id="cand_name"
                style={{
                  borderBottom: "1px solid #000",
                  margin: "0px 0 12px 0",
                }}
              ></div>

              {/* 2 FATHER / HUSBAND */}
              <div style={{ fontWeight: 700, fontSize: "12px" }}>
                2. FATHER’S / HUSBAND’S NAME
              </div>
              <div
                id="father_name"
                style={{
                  borderBottom: "1px solid #000",
                  margin: "0px 0 12px 0",
                }}
              ></div>

              {/* 3 MOTHER */}
              <div style={{ fontWeight: 700, fontSize: "12px" }}>
                3. MOTHER'S NAME
              </div>
              <div
                id="mother_name"
                style={{
                  borderBottom: "1px solid #000",
                  margin: "0px 0 12px 0",
                }}
              ></div>

              {/* Row of 5 small boxes */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "0.4fr 1.5fr 1.5fr 1.5fr 1.2fr",
                }}
              >
                {/* 4 GENDER */}
                <div
                  style={{
                    border: "1px solid #000",
                    padding: "6px 8px",
                    fontSize: "12px",
                    borderRight: "none",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      marginBottom: "6px",
                      fontSize: "12px",
                    }}
                  >
                    4. GENDER
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "1px",
                    }}
                  >
                    <span style={{ fontSize: "12px" }}>Male</span>
                    <input
                      id="gender_m"
                      type="checkbox"
                      style={{ width: "12px", height: "12px" }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: "12px" }}>Female</span>
                    <input
                      id="gender_f"
                      type="checkbox"
                      style={{ width: "12px", height: "12px" }}
                    />
                  </div>
                </div>

                {/* 5 DOB */}
                <div
                  style={{
                    border: "1px solid #000",
                    padding: "6px 8px",
                    fontSize: "12px",
                    borderRight: "none",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: "12px" }}>
                    5. DATE OF BIRTH
                  </div>
                  <div
                    style={{
                      fontSize: "8px",
                      opacity: 0.9,
                      marginBottom: "6px",
                    }}
                  >
                    (as in candidate’s Class X marksheet or equivalent
                    certificate)
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      id="dob_dd"
                      style={{
                        border: "1px solid #000",
                        width: "34px",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        borderRight: "none",
                        borderLeft: "none",
                        borderTop: "none",
                      }}
                    ></div>
                    <div
                      id="dob_mm"
                      style={{
                        border: "1px solid #000",
                        width: "34px",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        borderTop: "none",
                        borderRight: "none",
                      }}
                    ></div>
                    <div
                      id="dob_yy"
                      style={{
                        border: "1px solid #000",
                        width: "54px",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        borderTop: "none",
                        borderRight: "none",
                      }}
                    ></div>
                  </div>
                </div>

                {/* 6 CATEGORY */}
                <div
                  style={{
                    border: "1px solid #000",
                    padding: "6px 8px",
                    fontSize: "12px",
                    borderRight: "none",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      marginBottom: "4px",
                      fontSize: "12px",
                    }}
                  >
                    6. CATEGORY
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(6,auto)",
                      alignItems: "center",
                      fontSize: "12px",
                    }}
                  >
                    <span>GEN</span>
                    <input
                      id="cat_gen"
                      type="checkbox"
                      style={{ width: "12px", height: "12px" }}
                    />
                    <span>SC</span>
                    <input
                      id="cat_sc"
                      type="checkbox"
                      style={{ width: "12px", height: "12px" }}
                    />
                    <span>Minority</span>
                    <input
                      id="cat_min"
                      type="checkbox"
                      style={{ width: "12px", height: "12px" }}
                    />
                    <span>OBC</span>
                    <input
                      id="cat_obc"
                      type="checkbox"
                      style={{ width: "12px", height: "12px" }}
                    />
                    <span>ST</span>
                    <input
                      id="cat_st"
                      type="checkbox"
                      style={{ width: "12px", height: "12px" }}
                    />
                  </div>
                </div>

                {/* 7 NATIONALITY */}
                <div
                  style={{
                    border: "1px solid #000",
                    padding: "6px 8px",
                    fontSize: "12px",
                    borderRight: "none",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      marginBottom: "6px",
                      fontSize: "12px",
                    }}
                  >
                    7. NATIONALITY
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "4px 0",
                    }}
                  >
                    <span style={{ fontSize: "12px" }}>Indian</span>
                    <input
                      id="nat_indian"
                      type="checkbox"
                      style={{ width: "12px", height: "12px" }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span style={{ fontSize: "12px" }}>Others</span>
                    <span
                      id="nat_others"
                      style={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        height: "16px",
                      }}
                    ></span>
                  </div>
                </div>

                {/* 8 MARITAL STATUS */}
                <div
                  style={{
                    border: "1px solid #000",
                    padding: "6px 8px",
                    fontSize: "12px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      marginBottom: "6px",
                      fontSize: "12px",
                    }}
                  >
                    8. MARITAL STATUS
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "4px 0",
                    }}
                  >
                    <span style={{ fontSize: "12px" }}>Single</span>
                    <input
                      id="mar_single"
                      type="checkbox"
                      style={{ width: "12px", height: "12px" }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: "12px" }}>Married</span>
                    <input
                      id="mar_married"
                      type="checkbox"
                      style={{ width: "12px", height: "12px" }}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* 9. PERMANENT ADDRESS */}
            <section style={{ marginTop: "14px" }}>
              <div style={{ fontWeight: 700, fontSize: "12px" }}>
                9. PERMANENT ADDRESS{" "}
                <span style={{ fontWeight: 400, fontSize: "12px" }}>
                  (if different from correspondence address)
                </span>
              </div>
              <div
                style={{
                  border: "1px solid #000",
                  padding: "8px 10px",
                  fontSize: "12px",
                  marginTop: "6px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    alignItems: "center",
                    margin: "4px 0",
                  }}
                >
                  <span style={{ minWidth: "38px" }}>Address&nbsp;:</span>
                  <span
                    id="p_addr1"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                </div>
                <div
                  id="p_addr2"
                  style={{
                    borderBottom: "1px solid #000",
                    height: "16px",
                    margin: "6px 0",
                    marginLeft: "56px",
                  }}
                ></div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    margin: "6px 0",
                  }}
                >
                  <span
                    id="p_area"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                      marginLeft: "56px",
                    }}
                  ></span>
                  <span style={{ minWidth: "34px", textAlign: "right" }}>
                    City:
                  </span>
                  <span
                    id="p_city"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    margin: "6px 0px",
                  }}
                >
                  <span style={{ minWidth: "46px" }}>State&nbsp;:</span>
                  <span
                    id="p_state"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                  <span
                    style={{
                      minWidth: "64px",
                      textAlign: "right",
                      marginLeft: "63px",
                    }}
                  >
                    PIN / ZIP Code&nbsp;:
                  </span>
                  <span
                    id="p_pin"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    margin: "6px 0",
                  }}
                >
                  <span style={{ minWidth: "38px" }}>Country&nbsp;:</span>
                  <span
                    id="p_country"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                </div>
              </div>
            </section>

            {/* 10. CORRESPONDENCE ADDRESS */}
            <section style={{ marginTop: "14px" }}>
              <div style={{ fontWeight: 700, fontSize: "12px" }}>
                10. CORRESPONDENCE ADDRESS
              </div>
              <div
                style={{
                  border: "1px solid #000",
                  padding: "8px 10px",
                  fontSize: "12px",
                  marginTop: "6px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    alignItems: "center",
                    margin: "4px 0",
                  }}
                >
                  <span style={{ minWidth: "38px" }}>Address&nbsp;:</span>
                  <span
                    id="c_addr1"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                </div>
                <div
                  id="c_addr2"
                  style={{
                    borderBottom: "1px solid #000",
                    height: "16px",
                    margin: "6px 0",
                    marginLeft: "56px",
                  }}
                ></div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    margin: "6px 0",
                  }}
                >
                  <span
                    id="c_area"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                      marginLeft: "56px",
                    }}
                  ></span>
                  <span style={{ minWidth: "34px", textAlign: "right" }}>
                    City:
                  </span>
                  <span
                    id="c_city"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    margin: "6px 0px",
                  }}
                >
                  <span style={{ minWidth: "46px" }}>State&nbsp;:</span>
                  <span
                    id="c_state"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                  <span
                    style={{
                      minWidth: "64px",
                      textAlign: "right",
                      marginLeft: "63px",
                    }}
                  >
                    PIN / ZIP Code&nbsp;:
                  </span>
                  <span
                    id="c_pin"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    margin: "6px 0",
                  }}
                >
                  <span style={{ minWidth: "38px" }}>Country&nbsp;:</span>
                  <span
                    id="c_country"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                </div>
              </div>
            </section>

            {/* 11. E-MAIL ADDRESS */}
            <section style={{ marginTop: "14px" }}>
              <div style={{ fontWeight: 700, fontSize: "12px" }}>
                11. E-MAIL ADDRESS{" "}
                <span style={{ fontWeight: 400, fontSize: "11px" }}>
                  (Only in CAPITAL LETTERS)
                </span>
              </div>
              <div
                style={{
                  border: "1px solid #000",
                  padding: "8px 10px",
                  fontSize: "12px",
                  marginTop: "6px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    margin: "6px 0",
                  }}
                >
                  <span style={{ minWidth: "38px" }}>E&nbsp;mail&nbsp;:</span>
                  <span
                    id="email_caps"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      height: "16px",
                    }}
                  ></span>
                </div>
              </div>
            </section>

            {/* 12. CONTACT NUMBERS */}
            <section style={{ marginTop: "14px" }}>
              <div style={{ fontWeight: 700, fontSize: "12px" }}>
                12. CONTACT NUMBERS
              </div>
              <div
                style={{
                  border: "1px solid #000",
                  padding: "8px 10px",
                  fontSize: "12px",
                  marginTop: "6px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 2fr 2fr",
                    gap: "10px",
                    alignItems: "start",
                  }}
                >
                  {/* Country Code + Mobile */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <span>Country Code</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                      }}
                    >
                      <span>+</span>
                      <span
                        id="ccode"
                        style={{
                          flex: 1,
                          borderBottom: "1px solid #000",
                          height: "16px",
                        }}
                      ></span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      position: "relative",
                    }}
                  >
                    <div>Mobile Number</div>
                    <div
                      id="mobile"
                      style={{ borderBottom: "1px solid #000", height: "16px" }}
                    ></div>
                    <div
                      style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-6px",
                        width: 0,
                        height: "50px",
                        borderRight: "2px dashed #999",
                      }}
                    ></div>
                  </div>

                  {/* Landline with STD */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    <div>Landline phone number with STD (area) code</div>
                    <div
                      id="landline"
                      style={{ borderBottom: "1px solid #000", height: "16px" }}
                    ></div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div style={{ breakBefore: "page" }}></div>

        {/* ------------------------------ PAGE 2 ------------------------------ */}
        <div
          className="sheet"
          style={{
            width: "210mm",
            minHeight: "297mm",
            margin: "10mm auto",
            background: "#fff",
            boxShadow: "0 4px 18px rgba(0,0,0,.15)",
            display: "flex",
            flexDirection: "column",
            padding: "5mm",
          }}
        >
          <div
            className="sheet__inner"
            style={{
              padding: "5mm",
              border: "solid",
              borderLeft: "solid 1.5mm",
              borderRight: "solid 1.5mm",
            }}
          >
            {/* 14. EDUCATIONAL QUALIFICATIONS */}
            <section>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "12px",
                  marginBottom: "4px",
                }}
              >
                14. EDUCATIONAL QUALIFICATIONS <br />
                <span style={{ fontSize: "12px", fontWeight: 400 }}>
                  It is mandatory to fill all details of educational
                  qualification (as applicable) from Class X onwards till last
                  completed semester/year. If the system of marking of your
                  school/college is on grading pattern, convert grade into marks
                  as applicable and fill in the respective columns. In absence
                  of complete information, form will be rejected.
                </span>
              </div>
              <div
                style={{ borderBottom: "1px solid #000", margin: "0 0 9px 0" }}
              ></div>

              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "10px",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "5px",
                        width: "11%",
                        textAlign: "center",
                      }}
                    >
                      Examination
                      <br />
                      Passed
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "5px",
                        textAlign: "center",
                        width: "16%",
                      }}
                    >
                      Stream
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "center",
                        width: "19%",
                      }}
                    >
                      Name of the <br />
                      School / college and
                      <br />
                      Board / University
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "center",
                        width: "10%",
                      }}
                    >
                      Year of
                      <br />
                      passing
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "center",
                        width: "16%",
                      }}
                    >
                      Subject Studies
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "center",
                        width: "8%",
                      }}
                    >
                      Marks
                      <br />
                      Obtained
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "center",
                        width: "8%",
                      }}
                    >
                      Maximum
                      <br />
                      Marks
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "center",
                        width: "8%",
                      }}
                    >
                      % of
                      <br />
                      Marks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      style={{ border: "1px solid #000", padding: "5px 6px" }}
                    >
                      10th
                    </td>
                    <td
                      id="edu_10_stream"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_10_inst"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_10_year"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_10_sub"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_10_marks"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_10_max"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_10_pct"
                      style={{ border: "1px solid #000" }}
                    ></td>
                  </tr>
                  <tr>
                    <td
                      style={{ border: "1px solid #000", padding: "5px 6px" }}
                    >
                      12th
                    </td>
                    <td
                      id="edu_12_stream"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_12_inst"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_12_year"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_12_sub"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_12_marks"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_12_max"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_12_pct"
                      style={{ border: "1px solid #000" }}
                    ></td>
                  </tr>
                  <tr>
                    <td
                      style={{ border: "1px solid #000", padding: "5px 6px" }}
                    >
                      Graduation
                    </td>
                    <td
                      id="edu_grad_stream"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_grad_inst"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_grad_year"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_grad_sub"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_grad_marks"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_grad_max"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_grad_pct"
                      style={{ border: "1px solid #000" }}
                    ></td>
                  </tr>
                  <tr>
                    <td
                      style={{ border: "1px solid #000", padding: "5px 6px" }}
                    >
                      Post Graduation
                    </td>
                    <td
                      id="edu_pg_stream"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_pg_inst"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_pg_year"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_pg_sub"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_pg_marks"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_pg_max"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_pg_pct"
                      style={{ border: "1px solid #000" }}
                    ></td>
                  </tr>
                  <tr>
                    <td
                      style={{ border: "1px solid #000", padding: "5px 6px" }}
                    >
                      Any Other
                      <br />
                      Qualification
                    </td>
                    <td
                      id="edu_other_stream"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_other_inst"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_other_year"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_other_sub"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_other_marks"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_other_max"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="edu_other_pct"
                      style={{ border: "1px solid #000" }}
                    ></td>
                  </tr>
                </tbody>
              </table>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>Provisionally Admitted :</span>
                <span>Yes</span>
                <input
                  id="prov_yes"
                  type="checkbox"
                  style={{ width: "12px", height: "12px" }}
                />
                <span>No</span>
                <input
                  id="prov_no"
                  type="checkbox"
                  style={{ width: "12px", height: "12px" }}
                />
              </div>
            </section>

            <div
              style={{ borderBottom: "1px solid #000", margin: "12px 0" }}
            ></div>

            {/* 15. FAMILY INFORMATION */}
            <section>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "12px",
                  marginBottom: "4px",
                }}
              >
                15. FAMILY INFORMATION
              </div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "10px",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        width: "10.75%",
                        border: "1px solid #000",
                        padding: "5px 6px",
                        textAlign: "center",
                      }}
                    >
                      Relation
                    </th>
                    <th
                      style={{
                        width: "28.49%",
                        border: "1px solid #000",
                        padding: "5px 6px",
                        textAlign: "center",
                      }}
                    >
                      Name
                    </th>
                    <th
                      style={{
                        width: "12.36%",
                        border: "1px solid #000",
                        padding: "5px 6px",
                        textAlign: "center",
                      }}
                    >
                      Qualification
                    </th>
                    <th
                      style={{
                        width: "10.75%",
                        border: "1px solid #000",
                        padding: "5px 6px",
                        textAlign: "center",
                      }}
                    >
                      Occupation
                    </th>
                    <th
                      style={{
                        width: "10.75%",
                        border: "1px solid #000",
                        padding: "5px 6px",
                        textAlign: "center",
                      }}
                    >
                      Salary
                    </th>
                    <th
                      style={{
                        width: "26.88%",
                        border: "1px solid #000",
                        padding: "5px 6px",
                        textAlign: "center",
                      }}
                    >
                      Contact No.
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      id="fam1_rel"
                      style={{ border: "1px solid #000", height: "22px" }}
                    ></td>
                    <td
                      id="fam1_name"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="fam1_qual"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td id="fam1_occ" style={{ border: "1px solid #000" }}></td>
                    <td id="fam1_sal" style={{ border: "1px solid #000" }}></td>
                    <td
                      id="fam1_phone"
                      style={{ border: "1px solid #000" }}
                    ></td>
                  </tr>
                  <tr>
                    <td
                      id="fam2_rel"
                      style={{ border: "1px solid #000", height: "22px" }}
                    ></td>
                    <td
                      id="fam2_name"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="fam2_qual"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td id="fam2_occ" style={{ border: "1px solid #000" }}></td>
                    <td id="fam2_sal" style={{ border: "1px solid #000" }}></td>
                    <td
                      id="fam2_phone"
                      style={{ border: "1px solid #000" }}
                    ></td>
                  </tr>
                </tbody>
              </table>
            </section>

            <div
              style={{ borderBottom: "1px solid #000", margin: "12px 0" }}
            ></div>

            {/* 16. PAYMENT DETAILS */}
            <section>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "12px",
                  marginBottom: "4px",
                }}
              >
                16. PAYMENT DETAILS
              </div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "10px",
                  tableLayout: "fixed",
                }}
              >
                <colgroup>
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "40%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      &nbsp;
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      No.
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      Amount
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      Date
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      Drawn on (Bank Name)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "3px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          id="pay_cheque"
                          type="checkbox"
                          style={{ width: "12px", height: "12px" }}
                        />{" "}
                        Cheque/DD
                      </div>
                    </td>
                    <td
                      id="pay_cheque_no"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="pay_cheque_amt"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="pay_cheque_date"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="pay_cheque_bank"
                      style={{ border: "1px solid #000" }}
                    ></td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "3px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          id="pay_online"
                          type="checkbox"
                          style={{ width: "12px", height: "12px" }}
                        />{" "}
                        Online
                      </div>
                    </td>
                    <td
                      id="pay_online_no"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="pay_online_amt"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="pay_online_date"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="pay_online_bank"
                      style={{ border: "1px solid #000" }}
                    ></td>
                  </tr>
                  <tr style={{ position: "relative" }}>
                    <td style={{ border: "1px solid #000" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          id="pay_cash"
                          type="checkbox"
                          style={{ width: "12px", height: "12px" }}
                        />{" "}
                        Cash
                      </div>
                    </td>
                    <td
                      style={{ border: "1px solid #000", position: "relative" }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          right: 0,
                          bottom: 0,
                          background:
                            "linear-gradient(11deg,transparent 45%,#000 50%,#000 51%,transparent 50%)",
                        }}
                      ></div>
                    </td>
                    <td
                      id="pay_cash_amt"
                      style={{ border: "1px solid #000" }}
                    ></td>
                    <td
                      id="pay_cash_date"
                      style={{ border: "1px solid #000" }}
                    ></td>
                  </tr>
                </tbody>
              </table>

              <div style={{ marginTop: "12mm", textAlign: "right" }}>
                <span
                  style={{
                    display: "inline-block",
                    borderTop: "1px solid #000",
                    paddingTop: "4px",
                    minWidth: "72mm",
                    fontSize: "12px",
                    textAlign: "center",
                  }}
                >
                  Admission Incharge (Name & Signature)
                </span>
              </div>
            </section>

            {/* 17. RULES... */}
            <section style={{ marginTop: "20px" }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "12px",
                  marginBottom: "4px",
                }}
              >
                17. RULES, REGULATIONS / TERMS:
              </div>
              <ol
                style={{
                  margin: 0,
                  paddingLeft: "16px",
                  fontSize: "12px",
                  lineHeight: 1.45,
                }}
              >
                <li>
                  <strong>
                    The fees paid is neither refundable and nor adjustable under
                    any circumstances.
                  </strong>
                </li>
                <li>
                  Failure to pay fees by due date shall result in imposition of
                  fine.
                </li>
                <li>
                  Batch transfer is not permitted. Under special circumstances,
                  if allowed the student shall be required to pay the requisite
                  transfer fees.
                </li>
                <li>
                  The University reserves the right to make any change/amendment
                  in terms and conditions regarding admission at any time.
                </li>
                <li>
                  A student is required to inform the University about any
                  change in his/her contact details including
                  permanent/correspondence. /PG address, email-id at any point
                  of time.
                </li>
                <li>
                  The university may ask a student for submission of
                  certificates or documents at time of the admission or
                  afterwards, for the purpose of admission or otherwise, as
                  decided by the University.
                </li>
                <li>
                  The University reserves the right to cancel any admission for
                  non-submission of relevant documents.
                </li>
                <li>
                  Students should keep himself/herself well versed with the
                  updates in the University through our notice boards /website
                  and ISBM University is not responsible for any individual
                  information or intimation.
                </li>
                <li>
                  In the event of any disputes between the parties the courts of
                  Gariyaband shall have exclusive jurisdiction.
                </li>
              </ol>
            </section>

            <div
              style={{ borderBottom: "1px solid #000", margin: "12px 0" }}
            ></div>

            {/* 18. DECLARATION */}
            <section>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "12px",
                  marginBottom: "4px",
                }}
              >
                18. DECLARATION:
              </div>
              <div
                style={{
                  fontSize: "12px",
                  lineHeight: 1.5,
                  textAlign: "justify",
                }}
              >
                (To be filled by student) : I
                <span
                  id="decl_name"
                  style={{
                    display: "inline-block",
                    minWidth: "60mm",
                    borderBottom: "1px solid #000",
                  }}
                ></span>
                , Son / daughter of
                <span
                  id="decl_father"
                  style={{
                    display: "inline-block",
                    minWidth: "60mm",
                    borderBottom: "1px solid #000",
                  }}
                ></span>
                have read & hereby certify that the information given in the
                application is complete and correct to the best of my
                knowledge...
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-end",
                  marginTop: "15mm",
                  fontSize: "12px",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  Student Signature :
                  <span
                    id="decl_sign"
                    style={{
                      display: "inline-block",
                      minWidth: "50mm",
                      borderBottom: "1px solid #000",
                    }}
                  ></span>
                </div>

                <div>
                  Date:
                  <span
                    id="decl_date"
                    style={{
                      display: "inline-block",
                      minWidth: "50mm",
                      borderBottom: "1px solid #000",
                    }}
                  ></span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handlePrint}>
          Print
        </Button>
        <Button variant="success" onClick={onProceed}>
          Proceed to Pay
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ------------------------------ Usage example ------------------------------

// In your parent component after the form submit:

import React, { useState } from 'react';
import ApplicationPdfModal from './ApplicationPdfModal';

function Parent(){
  const [previewOpen, setPreviewOpen] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  async function handleFormSubmit(payload){
    // ...your submit logic
    setSubmitted(payload); // the JSON you provided
    setPreviewOpen(true);
  }

  return (
    <>
      <YourForm onSubmit={handleFormSubmit} />
      <ApplicationPdfModal show={previewOpen} onHide={()=>setPreviewOpen(false)} data={submitted} />
    </>
  );
}

*/
